import type { SVGProps } from "react";

const paths = {
  back: <path d="M20 12H4.5M11 5.5 4.5 12l6.5 6.5" />,
  bag: (
    <>
      <path d="M6.2 8.2h11.6l.9 11.4a1.9 1.9 0 0 1-1.9 2H7.2a1.9 1.9 0 0 1-1.9-2Z" />
      <path d="M9 8V6.4a3 3 0 0 1 6 0V8" />
    </>
  ),
  bell: (
    <>
      <path d="M18 9.4a6 6 0 1 0-12 0c0 5.4-2.2 6.6-2.2 6.6h16.4S18 14.8 18 9.4" />
      <path d="M10.2 19.6a2 2 0 0 0 3.6 0" />
    </>
  ),
  cash: (
    <>
      <rect x="3" y="6.6" width="18" height="10.8" rx="2.2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6.4 9.4v.01M17.6 14.6v.01" />
    </>
  ),
  check: <path d="m5.2 12.6 4.4 4.4L18.8 7.4" />,
  chevron: <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M12 7.6V12l3 2" />
    </>
  ),
  food: (
    <>
      <path d="M4.6 10.2a7.4 6.4 0 0 1 14.8 0Z" />
      <path d="M4.6 13.4h14.8M4.6 16.6h14.8v.6a2.6 2.6 0 0 1-2.6 2.6H7.2a2.6 2.6 0 0 1-2.6-2.6Z" />
    </>
  ),
  heart: (
    <path d="M20.2 5.8a5.2 5.2 0 0 0-7.4 0L12 6.6l-.8-.8a5.2 5.2 0 1 0-7.4 7.4l.8.8L12 21l7.4-7 .8-.8a5.2 5.2 0 0 0 0-7.4Z" />
  ),
  home: (
    <>
      <path d="M3.5 10.5 12 3.6l8.5 6.9V20a1.6 1.6 0 0 1-1.6 1.6H15v-6.4H9v6.4H5.1A1.6 1.6 0 0 1 3.5 20Z" />
    </>
  ),
  minus: <path d="M5.2 12h13.6" />,
  orders: (
    <>
      <path d="M6.5 3.5h11V21l-2.75-1.9L12 21l-2.75-1.9L6.5 21Z" />
      <path d="M9.5 8.5h5M9.5 12h5" />
    </>
  ),
  pharmacy: (
    <>
      <path d="M9.2 4.2a4.9 4.9 0 0 1 6.9 6.9l-5 5a4.9 4.9 0 1 1-6.9-6.9Z" />
      <path d="m12.4 7.4 4.2 4.2" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21.2S5.2 15 5.2 10a6.8 6.8 0 0 1 13.6 0c0 5-6.8 11.2-6.8 11.2Z" />
      <circle cx="12" cy="9.8" r="2.4" />
    </>
  ),
  plus: <path d="M12 5.2v13.6M5.2 12h13.6" />,
  repeat: (
    <>
      <path d="M17 2.8 20.4 6.2 17 9.6" />
      <path d="M3.6 11.2V9.4a3.2 3.2 0 0 1 3.2-3.2h13.6" />
      <path d="m7 21.2-3.4-3.4L7 14.4" />
      <path d="M20.4 12.8v1.8a3.2 3.2 0 0 1-3.2 3.2H3.6" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m16.6 16.6 4.4 4.4" />
    </>
  ),
  send: <path d="M21 3.4 10.6 13.8M21 3.4l-6.8 17.4-3.6-7.2L3.4 10Z" />,
  shop: (
    <>
      <path d="M5 9.4 6.4 4.4h11.2L19 9.4M5.2 9.6V19a1.6 1.6 0 0 0 1.6 1.6h10.4a1.6 1.6 0 0 0 1.6-1.6V9.6" />
      <path d="M9.8 20.4v-5.6h4.4v5.6" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8.2" r="3.9" />
      <path d="M4.8 20.4a7.6 7.6 0 0 1 14.4 0" />
    </>
  ),
  wallet: (
    <>
      <rect x="3" y="5.8" width="18" height="13.4" rx="3" />
      <path d="M21 11h-4.2a2.1 2.1 0 0 0 0 4.2H21" />
    </>
  ),
} as const;

export type IconName = keyof typeof paths;

export function Icon({ name, ...props }: SVGProps<SVGSVGElement> & { name: IconName }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" {...props}>
      {paths[name]}
    </svg>
  );
}

export function Brand({ light = false }: { light?: boolean }) {
  return (
    <span className={`brand ${light ? "light" : ""}`} aria-label="GoBro" dir="ltr" role="img">
      <b className="brand-g">G</b>
      <svg className="brand-disc" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="11.4" />
        <path
          d="M21 3.4 3.8 10.6c-.77.32-.72 1.43.08 1.68l6.3 1.97a1 1 0 0 1 .66.66l1.97 6.3c.25.8 1.36.85 1.68.08L21.6 4c.3-.72-.42-1.44-1.14-1.14Z"
          transform="translate(4.4 4.4) scale(.62)"
        />
      </svg>
      <b className="brand-bro">Bro</b>
    </span>
  );
}
